import type { PortalInvoiceSummary, PortalListResult } from '@erp/shared';
import { Download, FileText } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge } from '../../components/shadcn/index.js';
import { Card, DataTable, type DataTableColumn, EmptyState, Skeleton } from '../../components/ui/index.js';
import { customerApi } from '../../lib/customerApi.js';
import { formatMoney } from '../../lib/money.js';

import { downloadPortalPdf } from './download.js';

/**
 * The invoice table, header-less — it renders as a TAB of the merged
 * statement page now (statement · invoices · payments in one place), so the
 * page chrome lives there. The old /portal/invoices route redirects in.
 */
export function PortalInvoicesPanel(): JSX.Element {
  const { t, i18n } = useTranslation('portal');
  const [data, setData] = useState<PortalListResult<PortalInvoiceSummary> | null>(null);

  useEffect(() => {
    void customerApi()
      .get<PortalListResult<PortalInvoiceSummary>>('/invoices', { params: { limit: 200 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0 }));
  }, []);

  const columns: DataTableColumn<PortalInvoiceSummary>[] = useMemo(
    () => [
      {
        id: 'number',
        header: t('columns.number'),
        sortValue: (r) => r.docNumber,
        searchValue: (r) => r.docNumber,
        cell: (r) => (
          <Link to={`/portal/invoices/${r.id}`} className="font-mono text-xs text-brand hover:underline">
            {r.docNumber}
          </Link>
        ),
      },
      { id: 'date', header: t('columns.date'), sortValue: (r) => r.docDate, cell: (r) => r.docDate },
      { id: 'due', header: t('columns.due'), sortValue: (r) => r.dueDate ?? '', cell: (r) => r.dueDate ?? '—' },
      {
        id: 'status',
        header: t('columns.status'),
        sortValue: (r) => r.status,
        cell: (r) => (
          <Badge variant={r.status === 'paid' ? 'success' : r.status === 'cancelled' ? 'secondary' : 'warning'}>
            {t(`status.${r.status}`)}
          </Badge>
        ),
      },
      {
        id: 'total',
        header: t('columns.amount'),
        align: 'end',
        cellClassName: 'font-mono tabular-nums',
        sortValue: (r) => Number(r.total),
        cell: (r) => formatMoney(r.total, { locale: i18n.language, currency: r.currencyCode }),
      },
      {
        id: 'balance',
        header: t('columns.balance'),
        align: 'end',
        cellClassName: 'font-mono tabular-nums',
        sortValue: (r) => Number(r.balance),
        cell: (r) => formatMoney(r.balance, { locale: i18n.language, currency: r.currencyCode }),
      },
      {
        id: 'actions',
        header: '',
        align: 'end',
        cell: (r) => (
          <button
            type="button"
            onClick={() => void downloadPortalPdf(`/invoices/${r.id}/pdf`, `${r.docNumber}.pdf`, t('loadError'))}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-fg-muted hover:bg-bg-subtle"
            aria-label={t('invoices.download')}
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            PDF
          </button>
        ),
      },
    ],
    [t, i18n.language],
  );

  return !data ? (
    <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>
  ) : data.items.length === 0 ? (
    <EmptyState icon={<FileText className="h-8 w-8" aria-hidden />} title={t('invoices.empty')} description={t('invoices.emptyDescription')} />
  ) : (
    <Card className="overflow-hidden p-0">
      <DataTable columns={columns} data={data.items} rowKey={(r) => r.id} searchPlaceholder={t('invoices.searchPlaceholder')} />
    </Card>
  );
}
